/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/no-array-index-key */
import React, { lazy } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import { Routing } from 'shared/constants/routing';
import { GuestLayout, MainLayout } from 'layout';
import { IApplicationState, isNotAuthenticated, isUserAuthenticated } from 'store/state/app-state';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import ServiceUnavailable from './components/ServiceUnavailable';
import { AuthActionType } from 'store/actionTypes/auth-action-type';
import SessionTimeoutManager from 'layout/SessionTimeout/SessionTimeoutManager';

const Login = lazy(() => import('pages/Account/Login'));
const SignUp = lazy(() => import('pages/Account/SignUp'));
const ForgotPassword = lazy(() => import('pages/Account/ForgotPassword'));
const ResetPassword = lazy(() => import('pages/Account/ResetPassword'));
const TimeLine = lazy(() => import('pages/TimeLine/TimeLine'));
const ClinicalSummary = lazy(() => import('pages/ClinicalSummary/ClinicalSummary'));
const Appointment = lazy(() => import('pages/Appointment/Appointment'));
const D3Graph = lazy(() => import('pages/UI/D3Graph'));
const Labs = lazy(() => import('pages/Lab/Labs'));
// const Images = lazy(() => import('pages/Image/Images'));
const Vitals = lazy(() => import('pages/Vital/Vitals'));
const Messages = lazy(() => import('pages/Message/Messages'));
const Questionnaires = lazy(() => import('pages/Questionnaire/Questionnaires'));
const PatientForms = lazy(() => import('pages/PatientForm/PatientForms'));
const Immunizations = lazy(() => import('pages/Immunization/Immunizations'));
const Documents = lazy(() => import('pages/Document/Documents'));
const MedicalRecords = lazy(() => import('pages/MedicalRecord/MedicalRecords'));
const MembershipPlans = lazy(() => import('pages/MembershipPlans/MembershipPlans'));
const CarePlans = lazy(() => import('pages/MembershipPlans/CarePlans'));
const Medications = lazy(() => import('pages/Medication/Medications'));
const Allergies = lazy(() => import('pages/Allergy/Allergies'));
const Problems = lazy(() => import('pages/ProblemList/ProblemRecords'));
const Procedures = lazy(() => import('pages/Procedure/Procedures'));
const Insurances = lazy(() => import('pages/Insurance/Insurances'));
const Providers = lazy(() => import('pages/Provider/Providers'));
const Facilities = lazy(() => import('pages/Facility/Facilities'));
const Pharmacies = lazy(() => import('pages/Pharmacy/Pharmacies'));
const Invoice = lazy(() => import('pages/Billing/Invoices'));
const Diets = lazy(() => import('pages/Diet/Diets'));
const UrgentCareList = lazy(() => import('pages/UrgentCare/UrgentCareList'));
const VisitSummary = lazy(() => import('pages/VisitSummary/VisitSummary'));
const MyProfile = lazy(() => import('pages/MyProfile/MyProfile'));
const PatientForm = lazy(() => import('pages/MyProfile/PatientForm'));
const VideoCall = lazy(() => import('pages/VideoCall/index'));
const SMSQuestionnaire = lazy(() => import('pages/SMSQuestionnaire/SMSQuestionnaire'));
const SMSPatientForm = lazy(() => import('pages/SMSPatientForm/SMSPatientForm'));
const PatientBasicDetails = lazy(() => import('pages/PatientBasicdetails/PatientBasicdetails'));
const NotificationList = lazy(() => import('layout/NotificationList'));
const DocumentViewer = lazy(() => import('pages/DocumentViewer/DocumentViewer'));
const PatientFeedback = lazy(() => import('pages/Feedback/PatientFeedback'));
const Payment = lazy(() => import('pages/Payment/Payment'));
const PaymentError = lazy(() => import('pages/Payment/PaymentError'));
const PaymentSuccess = lazy(() => import('pages/Payment/PaymentSuccess'));
const SavedCards = lazy(() => import('pages/SavedCards/SavedCards'));

interface IRoute {
    path: string;
    component: React.LazyExoticComponent<React.FC<{ any }>> | React.ReactNode;
    isPrivateRoute?: boolean;
    hideHeader?: boolean;
    exact?: boolean;
    action?: string;
    page?: string;
    isPublicRoute?: boolean;
    isGuestRoute?: boolean;
}

const routes: IRoute[] = [
    {
        path: Routing.LogIn,
        component: <Login />,
        isGuestRoute: true,
    },
    {
        path: Routing.SignUp,
        component: <SignUp />,
        isGuestRoute: true,
    },
    {
        path: Routing.ForgotPassword,
        component: <ForgotPassword />,
        isGuestRoute: true,
    },
    {
        path: Routing.ResetPassword,
        component: <ResetPassword />,
        isGuestRoute: true,
    },
    {
        path: Routing.TimeLine,
        component: <TimeLine />,
        isPrivateRoute: true,
    },
    {
        path: Routing.clinicalSummary,
        component: <ClinicalSummary />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Appointment,
        component: <Appointment />,
        isPrivateRoute: true,
    },
    {
        path: Routing.UrgentCare,
        component: <UrgentCareList />,
        isPrivateRoute: true,
    },
    {
        path: Routing.D3Graph,
        component: <D3Graph />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Message,
        component: <Messages />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Questionnaire,
        component: <Questionnaires />,
        isPrivateRoute: true,
    },
    {
        path: Routing.PatientForms,
        component: <PatientForms />,
        isPrivateRoute: true,
    },
    {
        path: Routing.SMSQuestionnaire,
        component: <SMSQuestionnaire />,
        isPublicRoute: true,
    },
    {
        path: Routing.SMSPatientForm,
        component: <SMSPatientForm />,
        isPublicRoute: true,
    },
    {
        path: Routing.Document,
        component: <Documents />,
        isPrivateRoute: true,
    },
    {
        path: Routing.VisitSummary,
        component: <VisitSummary />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Allergy,
        component: <Allergies />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Problem,
        component: <Problems />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Medication,
        component: <Medications />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Labs,
        component: <Labs />,
        isPrivateRoute: true,
    },
    // {
    //     path: Routing.Images,
    //     component: <Images />,
    //     isPrivateRoute: true,
    // },
    {
        path: Routing.Vitals,
        component: <Vitals />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Diet,
        component: <Diets />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Procedure,
        component: <Procedures />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Insurance,
        component: <Insurances />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Provider,
        component: <Providers />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Facilities,
        component: <Facilities />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Pharmacy,
        component: <Pharmacies />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Billing,
        component: <Invoice />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Immunizations,
        component: <Immunizations />,
        isPrivateRoute: true,
    },
    {
        path: Routing.MyProfile,
        component: <MyProfile />,
        isPrivateRoute: true,
    },
    {
        path: Routing.EditPatient,
        component: <PatientForm />,
        isPrivateRoute: true,
    },
    {
        path: Routing.VideoSessionCall,
        component: <VideoCall />,
        isPrivateRoute: true,
        hideHeader: true,
    },
    {
        path: Routing.Notification,
        component: <NotificationList />,
        isPrivateRoute: true,
    },
    {
        path: Routing.DocumentViewer,
        component: <DocumentViewer />,
        hideHeader: true,
        isPrivateRoute: true,
    },
    {
        path: Routing.PatientDetails,
        component: <PatientBasicDetails />,
        isPrivateRoute: true,
    },
    {
        path: Routing.Payment,
        component: <Payment />,
        isPrivateRoute: true,
    },
    {
        path: Routing.PatientFeedback,
        component: <PatientFeedback />,
        isPublicRoute: true,
    },
    {
        path: Routing.PaymentSuccess,
        component: <PaymentSuccess />,
        isPrivateRoute: true,
    },
    {
        path: Routing.PaymentSuccessLink,
        component: <PaymentSuccess />,
        isPublicRoute: true,
    },
    {
        path: Routing.PaymentByLink,
        component: <Payment />,
        isPublicRoute: true,
    },
    {
        path: Routing.PaymentError,
        component: <PaymentError />,
        isPrivateRoute: true,
    },
    {
        path: Routing.PaymentErrorLink,
        component: <PaymentError />,
        isPublicRoute: true,
    },
    {
        path: Routing.MedicalRecords,
        component: <MedicalRecords />,
        isPrivateRoute: true,
    },
    {
        path: Routing.MembershipPlans,
        component: <MembershipPlans />,
        isPrivateRoute: true,
    },
    {
        path: Routing.CarePlans,
        component: <CarePlans />,
        isPrivateRoute: true,
    },
    {
        path: Routing.SavedCards,
        component: <SavedCards />,
        isPrivateRoute: true,
    },
];

const Routes: React.FC = () => {
    const userData = useSelector((state: IApplicationState) => state.UserData);
    const generalData = useSelector((state: IApplicationState) => state.GeneralData);
    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch({
            type: AuthActionType.SET_SERVICE_DOWN,
            payload: false,
        });
    }, [dispatch]);

    if (generalData?.isServiceDown) {
        return <ServiceUnavailable />;
    }

    return (
        <Router>
            <SessionTimeoutManager />
            <Switch>
                <Route path={routes.filter((route) => route.isGuestRoute).map((x) => x.path)}>
                    <GuestLayout>
                        <Switch>
                            {routes
                                .filter((route) => route.isGuestRoute)
                                .map(({ path, component }, key) => (
                                    <Route
                                        exact
                                        path={path}
                                        component={isNotAuthenticated(() => {
                                            toast.dismiss();
                                            return component;
                                        })}
                                        key={key}
                                    />
                                ))}
                        </Switch>
                    </GuestLayout>
                </Route>
                <Route
                    path={routes
                        .filter((route) => userData?.userIdEnc && route.isPrivateRoute && !route.hideHeader)
                        .map((x) => x.path)}
                >
                    <MainLayout>
                        <Switch>
                            {routes
                                .filter((route) => route.isPrivateRoute)
                                .map(({ path, component, exact = true }, key) => (
                                    <Route
                                        exact={exact}
                                        path={path}
                                        component={isUserAuthenticated(() => component)}
                                        key={key}
                                    />
                                ))}
                        </Switch>
                    </MainLayout>
                </Route>
                <Route path={routes.filter((route) => route.isPrivateRoute && route.hideHeader).map((x) => x.path)}>
                    <Switch>
                        {routes
                            .filter((route) => route.isPrivateRoute)
                            .map(({ path, component, exact = true }, key) => (
                                <Route exact={exact} path={path} component={() => component as any} key={key} />
                            ))}
                    </Switch>
                </Route>
                <Route path={routes.filter((route) => route.isPublicRoute).map((x) => x.path)}>
                    <Switch>
                        {routes
                            .filter((route) => route.isPublicRoute)
                            .map(({ path, component, exact = true }, key) => {
                                return (
                                    <Route
                                        exact={exact}
                                        path={path}
                                        component={() => {
                                            if (userData?.userIdEnc) {
                                                return (
                                                    <MainLayout>
                                                        <Switch>{component}</Switch>
                                                    </MainLayout>
                                                );
                                            }
                                            return component as any;
                                        }}
                                        key={key}
                                    />
                                );
                            })}
                    </Switch>
                </Route>
                <Redirect to={{ pathname: Routing.LogIn }} />
            </Switch>
        </Router>
    );
};

export default Routes;
